import { Attachment } from './attachment';
import { BTTYPE } from './binary-tree';
import { Comment } from './comment';
import { Connection } from './connection';
import { CoordinatorService } from './coordinator.service';
import { Databin } from './databin';
import { Keyword } from './keyword';
import { KeywordGroup } from './keyword-group';
import { Project } from './project';
import { StyleGroup } from './style-group';
import { View } from './view';
import { XItem } from './x-item';

export class ScriptInput {
    #coords: CoordinatorService;

    #memowl: any;

    constructor(coords: CoordinatorService) {
        this.#coords = coords;
    }

    private async _isXLSX(arrayBuffer: ArrayBuffer): Promise<boolean> {
        const view = new DataView(arrayBuffer);
        const requiredEntries = new Set(['[Content_Types].xml', '_rels/.rels', 'xl/workbook.xml']);

        let offset = 0;

        while (offset < arrayBuffer.byteLength - 30) {
            if (view.getUint32(offset, true) === 0x04034b50) {
                const fileNameLength = view.getUint16(offset + 26, true);
                const extraFieldLength = view.getUint16(offset + 28, true);
                const filename = new TextDecoder().decode(
                    new Uint8Array(arrayBuffer, offset + 30, fileNameLength),
                );
                requiredEntries.delete(filename);
                offset +=
                    30 + fileNameLength + extraFieldLength + view.getUint32(offset + 18, true);
                if (!requiredEntries.size) return true;
            } else {
                offset++;
            }
        }

        return false;
    }

    private _textTpTest(b: ArrayBuffer, maxRows = 100): 'csv' | 'bibtex' | 'json' | '' {
        const data = new Uint8Array(b);
        const isWhitespace = (byte: number) =>
            byte === 0x20 || // Space
            byte === 0x09 || // Horizontal Tab
            byte === 0x0a || // Line Feed (LF)
            byte === 0x0d; // Carriage Return (CR)

        const isSeparator = (byte: number) =>
            byte === 0x2c || // comma
            byte === 0x3b; // Semicolon

        let insideQuotes = false; // Tracks if we're inside quotes
        let currentSepCount = 0; // Comma count for the current line
        let isStart = true;
        const sepCountLs: number[] = [];

        for (let i = 0; i < data.length; i++) {
            const char = data[i];
            if (isStart && isWhitespace(char)) continue;
            if (isStart) {
                const str = new TextDecoder().decode(data.subarray(i, i + 50));
                if (str === '{') return 'json';
                if (str.startsWith('@') && str.includes('{')) return 'bibtex';
            }
            isStart = false;

            if (char === 0x22) {
                // ASCII code for "
                insideQuotes = !insideQuotes; // Toggle quote flag
            } else if (isSeparator(char) && !insideQuotes) {
                // ASCII code for ,
                currentSepCount++; // Count commas only outside quotes
            } else if ((char === 0x0a || char === 0x0d) && !insideQuotes) {
                // ASCII codes for \n and \r
                sepCountLs.push(currentSepCount);
                currentSepCount = 0;
            }

            if (sepCountLs.length >= maxRows) {
                break;
            }
        }

        // console.log('sepCountLs', sepCountLs);
        let isFirstChange = true;
        if (sepCountLs.length < 2) return '';
        for (let j = sepCountLs.length - 1; j > 0; j--) {
            if (sepCountLs[j] !== sepCountLs[j - 1] || sepCountLs[j] < 3) {
                if (!isFirstChange) return '';
                isFirstChange = false;
            }
        }
        return 'csv';
    }

    // Handle the last line if it doesn't end with a newline
    private _bibtex2memowl(data: string) {
        const c = this.#coords.initCiteEngineWithData(data);
        const items: any[] = [];
        JSON.parse(c.format('data')).forEach((x: any) => {
            const dct: any = {};
            items.push(dct);
            dct.id = x.id;
            dct.title = x.title;
            dct.url = x.URL;
            dct.publicationDate = this.#coords.utils$.datepart2ISO(x.issued);
            if (x.type === 'patent') {
                dct.patentNumber = x.number.replaceAll(' ', '').replaceAll('/', '');
                dct.inventorLs = x.author?.map((a: any) => `${a.family}, ${a.given}`);
            }
            console.log(888, dct);
        });

        return {
            ns: 'https://memowl.app',
            payload: { data: { items } },
        };
    }

    async process(f: File | Blob) {
        const buffer = await f.arrayBuffer();
        const isZip =
            buffer.byteLength >= 4 &&
            new Uint8Array(buffer, 0, 4)[0] === 0x50 &&
            new Uint8Array(buffer, 0, 4)[1] === 0x4b &&
            new Uint8Array(buffer, 0, 4)[2] === 0x03 &&
            new Uint8Array(buffer, 0, 4)[3] === 0x04;

        let finalResp: any;

        if (isZip) {
            if (await this._isXLSX(buffer)) {
                const resp = (await this.#coords.worker$.parser.call(
                    'excel-buffer',
                    buffer,
                )) as any;
                finalResp = resp.payload;
            } else {
                const resp = (await this.#coords.worker$.manifest.call(
                    'uncompress',
                    buffer,
                )) as any;
                finalResp = resp.payload;
            }
        } else {
            const tpTest = this._textTpTest(buffer);

            if (tpTest === 'csv') {
                const resp = (await this.#coords.worker$.parser.call('csv-buffer', buffer)) as any;
                finalResp = resp.payload;
            } else {
                finalResp = new TextDecoder().decode(buffer);
                if (tpTest === 'bibtex') {
                    finalResp = this._bibtex2memowl(finalResp);
                } else {
                    const start = finalResp.substring(0, 100).trim();
                    if (start.startsWith('{') && start.includes('lens_id')) {
                        const resp = (await this.#coords.worker$.parser.call('lens-jsonl', {
                            data: finalResp,
                        })) as any;
                        finalResp = resp.payload;
                    } else if (start.includes('Lens') && start.includes('Jurisdiction')) {
                        alert(
                            'Lens CVS is irregular. Please use different output format at https://lens.org',
                        );
                        return {};
                    }
                }
            }
        }

        if (typeof finalResp === 'string' && finalResp.startsWith('{'))
            try {
                finalResp = JSON.parse(finalResp);
            } catch (e) {}

        if (finalResp.error) alert(finalResp.error);

        if (finalResp.ns === 'https://memowl.app') this.#memowl = finalResp;

        return finalResp;
    }

    async asNew() {
        const p = this.#memowl.data?.project;
        if (!p) return;
        const pr = new Project(this.#coords, p);
        pr.isViewMode = true;
        await this.#coords.project$.clear();
        await this.#coords.project$.setCurrent(pr);
        this.importAll();
        this.#coords.project$.isDisplayOn = true;
        this.#coords.stage$.afterProjectLoaded(true, pr);
        this.#coords.display$.markForCheck();
        // console.log(456, pr);
    }

    async readFile(isMultiple = false) {
        const resp = await this.#coords.filesystem$.pickFiles();
        return isMultiple ? resp.map((f: File) => this.process(f)) : this.process(resp);
    }

    importAll() {
        this.importCollection({ subsets: true });
        this.importKeywords();
        this.importRatings();
        this.importTags();
        this.importDatabins();
        this.importComments();
        this.importAttachments();
        this.importAutousers();
        this.importScripts();
        return this;
    }

    importAutousers() {
        const auLs = this.#memowl.data.autousers;
        if (!auLs) return;

        auLs.forEach((auParams: any) => {
            console.log('importAutouser', auParams);
            const au = this.#coords.autouser$.get(auParams.name, true)!;
            auParams.isCurrentSnapshot && au.asSnapshot(true);
            au.import(auParams);
        });
    }

    importComments() {
        const cLs = this.#memowl.data.comments;
        if (!cLs) return;
        const cId = this.#coords.user$.currentId;
        cLs.forEach((cParams: any) => {
            if (!cParams.text || !cParams.originId) return;
            cParams.ownerId = cId;
            const c = new Comment(this.#coords, cParams);
            this.#coords.comment$.addComment(c);
        });
    }

    importAttachments() {
        const attLs = this.#memowl.data.attachments;
        if (!attLs) return;
        const cId = this.#coords.user$.currentId;
        attLs.forEach((attParams: any) => {
            attParams.ownerId = cId;
            const att = new Attachment(this.#coords, attParams);
            this.#coords.attachment$.add(att);
        });
    }

    importDatabins(markForSave = false) {
        const dtbLs = this.#memowl.data.databins;
        if (!dtbLs) {
            this.#coords.dataset$.preview.requestReduplicate();
            return;
        }
        const cId = this.#coords.user$.currentId;

        const rMp = new Map<string, any>();
        this.#memowl.data.resources?.forEach((r: any) => rMp.set(r.id, r));

        dtbLs.forEach((dtbParams: any) => {
            const dtb = new Databin(this.#coords, dtbParams);
            dtb.ownerId = cId;
            dtbParams.resources?.forEach((rId: string) => {
                const rParams = rMp.get(rId);
                if (!rParams) return;
                delete rParams.id;
                rParams.connector = rParams.type;
                rParams._databinId = dtb.id;
                rParams.xitemIdLs = rParams.items;
                const c = new Connection(this.#coords, rParams);
                c.intoProject();
                this.#xitmLs(rParams.items).forEach((xitm: XItem) =>
                    dtb.xitemMap.set(xitm.id, xitm),
                );
                markForSave && c.markForManifestStore();
            });
            markForSave && dtb.markForManifestStore();
        });
        this.#coords.databin$.afterLoad();
    }

    #importBT(tp: BTTYPE, includeValues: boolean) {
        const btLs = this.#memowl.data?.[tp];
        if (!btLs) return;
        btLs.forEach((btParams: any) => {
            this.#coords.binaryTree$.createBinaryTree(btParams);
        });
        if (includeValues) this.#importBTValues(tp);
        this.#coords.display$.markForCheck();
    }

    #importBTValues(tp: BTTYPE) {
        const btsLs = this.#memowl.data?.[`${tp}NodesItems`];
        if (!btsLs) return;
        btsLs.forEach((btParams: any) => {
            const bt = this.#coords.binaryTree$.getTree(btParams.id);
            if (!bt) return;
            bt.updateUserValues(btParams.nodesItems);
        });

        this.#coords.display$.markForCheck();
    }

    importRatings(includeValues = true) {
        return this.#importBT('ratings', includeValues);
    }

    importTags(includeValues = true) {
        return this.#importBT('tags', includeValues);
    }

    #xitmLs(ls: string[]) {
        const cId = this.#coords.user$.currentId;
        const s = new Set<string>(ls);
        return this.#memowl.data.items
            ?.map((itm: any) => {
                if (!s.has(itm.id)) return;
                itm.ownerId = cId;
                return new XItem(this.#coords, itm);
            })
            .filter((x: any) => x);
    }

    importCollection({ subsets }: { subsets?: boolean } = {}) {
        const c = this.#memowl?.data?.collection;

        if (!c) {
            this.#coords.dataset$.main.requestReduplicate();
            return;
        }

        const m = this.#coords.dataset$.main;
        m.importXitemLs(this.#xitmLs(c), true);
        m.markForManifestStore();
        subsets && this.#importSubsets();
        this.#coords.display$.markForCheck();
        return this;
    }

    importKeywords(isReplace = false) {
        this.importKeywordGroups();
        const kLs = this.#memowl?.data?.keywords;
        if (!kLs) return;
        // this.#coords.keyword$.importKeywordLs(kLs);
        const kwLs = kLs.map((kParams: any) => new Keyword(this.#coords, kParams));
        this.#coords.keyword$.addKeywords(kwLs, isReplace);
        this.#coords.display$.markForCheck();
        return this;
    }

    importScripts() {
        const scLs = this.#memowl?.scripts;
        if (!scLs) return;
        this.#coords.script$.load3({ scriptLs: scLs });
    }

    importKeywordGroups() {
        const kLs = this.#memowl?.data?.keywordGroups;
        if (!kLs) return;
        const stylesSet = new Set<string>();
        kLs.forEach((k: any) => {
            if (k.id.startsWith('_')) return;
            const kg = new KeywordGroup(this.#coords, k);
            this.#coords.keyword$.addKeywordGroup(kg);
            stylesSet.add(kg.styleId);
        });

        this.#memowl.data.styles?.forEach((st: any) => {
            if (!stylesSet.has(st.id)) return;
            const style = new StyleGroup(this.#coords, st);
            this.#coords.highlight$.setStyle(style);
        });
        return this;
    }

    #importSubsets() {
        const sLs = this.#memowl?.data?.subsets;
        if (!sLs) return;
        const vLs = sLs.map((s: any) => {
            const v = this.#coords.view$.getByTitle(s.title);
            if (v) {
                v.addIds(s.items);
                return v;
            } else return new View(this.#coords, s);
        });
        vLs.forEach((v: View) => {
            v.checkCollectionItems();
            v.markForManifestStore();
        });
        this.#coords.display$.markForCheck();
        return this;
    }

    log(prefix = 'Input') {
        console.log(`${prefix}:`, this);
    }
}
